import React, { useCallback } from 'react';

import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';

/** Maximum characters for a saved search name, matching the Search page. */
export const MAX_SAVED_SEARCH_NAME_LENGTH = 50;

interface SaveTrackSearchModalProps {
  readonly visible: boolean;
  readonly name: string;
  readonly onNameChange: (name: string) => void;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * Naming dialog for saving the current Track filter selection.
 * Mirrors the Search page's save modal.
 */
const SaveTrackSearchModal: React.FC<SaveTrackSearchModalProps> = ({
  visible,
  name,
  onNameChange,
  onConfirm,
  onCancel,
}) => {
  const isSaveDisabled = name.trim().length === 0;

  const handleChange = useCallback(
    (value: string) =>
      onNameChange(value.slice(0, MAX_SAVED_SEARCH_NAME_LENGTH)),
    [onNameChange],
  );

  return (
    <Modal
      visible={visible}
      onDismiss={onCancel}
      header="Save search"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={isSaveDisabled}
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <FormField
        label="Search name"
        constraintText={`${name.length}/${MAX_SAVED_SEARCH_NAME_LENGTH} characters`}
      >
        <Input
          value={name}
          onChange={({ detail }) => handleChange(detail.value)}
          placeholder="Enter a name for this saved search"
          ariaLabel="Saved search name"
        />
      </FormField>
    </Modal>
  );
};

export default SaveTrackSearchModal;
